// @ts-nocheck
import { useState } from "react";
import type { ReactNode } from "react";
import {
  Button, Modal, Form, Input, Select, Space,
  Typography, Descriptions, Divider, message, Dropdown,
} from "../components/admin-ui";
import {
  CheckCircleOutlined, CloseCircleOutlined, EyeOutlined,
  ShopOutlined, ReloadOutlined, MoreOutlined, ClockCircleOutlined,
} from "../components/admin-icons";
import { useTranslation } from "react-i18next";
import type { ColumnsType } from "../components/admin-ui";
import type { StallApplication } from "@kawkaw/shared";
import { DataTable } from "../components/DataTable";
import { AdminShell } from "../layouts/AdminShell";
import { useStallApplications, useStallApplication, useReviewStallApplication } from "../api/hooks";
import { tokens } from "../styles/tokens";
import { formatDateTimeDetail, formatDateTimeWithSeconds } from "../utils/formatLocaleDate";

const { Text } = Typography;
const { TextArea } = Input;
const { color: T } = tokens;

const STATUS_STYLE: Record<string, { color: string; bg: string }> = {
  pending: { color: T.brandAmberText, bg: T.warningBg },
  approved: { color: T.successText, bg: T.successBg },
  rejected: { color: T.dangerText, bg: T.dangerBg },
};

function ApplicationStatCard({
  label, value, icon, iconColor, badge, badgeColor, badgeBg,
}: {
  label: string;
  value: number;
  icon: ReactNode;
  iconColor: string;
  badge: string;
  badgeColor: string;
  badgeBg: string;
}) {
  return (
    <div className="admin-kpi-card admin-stall-application-stat">
      <div className="admin-kpi-heading flex">
        <div className="admin-kpi-label">{label}</div>
        <span className="admin-kpi-icon" style={{ color: iconColor, background: badgeBg }}>{icon}</span>
      </div>
      <div className="admin-kpi-value">{value.toLocaleString()}</div>
      <div className="admin-stall-application-stat-note" style={{ color: badgeColor }}>{badge}</div>
    </div>
  );
}

export function StallApplicationsScreen() {
  const { t, i18n } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [viewApp, setViewApp] = useState<StallApplication | null>(null);
  const [reviewModal, setReviewModal] = useState<{ app: StallApplication; decision: "approve" | "reject" } | null>(null);
  const [form] = Form.useForm();

  const { data, isLoading, refetch } = useStallApplications({ status: statusFilter, page, limit: 10 });
  const { data: allSummary } = useStallApplications({ page: 1, limit: 1 });
  const { data: pendingSummary } = useStallApplications({ status: "pending", page: 1, limit: 1 });
  const { data: approvedSummary } = useStallApplications({ status: "approved", page: 1, limit: 1 });
  const { data: rejectedSummary } = useStallApplications({ status: "rejected", page: 1, limit: 1 });
  // Fresh detail fetch when the drawer opens — picks up reviewer name + latest status if it changed in another tab.
  const { data: detailData } = useStallApplication(viewApp?.id ?? null);
  const liveApp = detailData?.data ?? viewApp;
  const review = useReviewStallApplication();

  const handleReview = async (values: { review_note?: string; sort_order?: number }) => {
    if (!reviewModal) return;
    try {
      await review.mutateAsync({
        id: reviewModal.app.id,
        decision: reviewModal.decision,
        review_note: values.review_note,
        sort_order: values.sort_order,
      });
      void message.success(
        reviewModal.decision === "approve"
          ? t("stallApplications.approvedMsg")
          : t("stallApplications.rejectedMsg"),
      );
      setReviewModal(null);
      form.resetFields();
    } catch {
      void message.error(t("stallApplications.failedMsg"));
    }
  };

  const columns: ColumnsType<StallApplication> = [
    {
      title: t("stallApplications.colCreatedAt"),
      dataIndex: "created_at",
      key: "created_at",
      width: 160,
      render: (v: string) => <span className="admin-data-time">{formatDateTimeDetail(v, i18n.language)}</span>,
    },
    {
      title: t("stallApplications.colStallName"),
      dataIndex: "stall_name",
      key: "stall_name",
      render: (v: string) => <span className="admin-data-customer">{v}</span>,
    },
    {
      title: t("stallApplications.colCategory"),
      dataIndex: "category",
      key: "category",
      render: (v: string | null) => v
        ? <span className="admin-data-badge admin-stall-application-category">{v}</span>
        : <span className="admin-data-secondary">—</span>,
    },
    {
      title: t("stallApplications.colContact"),
      key: "contact",
      render: (_: unknown, r: StallApplication) => (
        <div className="admin-stall-application-contact">
          <div className="admin-data-customer">{r.contact_name}</div>
          <div className="admin-data-secondary">{r.contact_phone}</div>
        </div>
      ),
    },
    {
      title: t("stallApplications.colStatus"),
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (v: string) => {
        const style = STATUS_STYLE[v] ?? { color: T.textSecondary, bg: T.surfaceSection };
        return (
          <span className="admin-data-badge" style={{ color: style.color, background: style.bg }}>
            {t(`stallApplications.status.${v}`, { defaultValue: v })}
          </span>
        );
      },
    },
    {
      title: t("stallApplications.colReviewer"),
      key: "reviewer",
      width: 120,
      render: (_: unknown, r: StallApplication) =>
        r.reviewer ? <span className="admin-data-customer">{r.reviewer.name}</span> : <span className="admin-data-secondary">—</span>,
    },
    {
      title: t("stallApplications.colActions"),
      key: "actions",
      width: 96,
      fixed: "right",
      render: (_: unknown, r: StallApplication) => (
        <Dropdown
          menu={{
            items: [
              { key: "view", icon: <EyeOutlined />, label: t("stallApplications.viewDetail"), onClick: () => setViewApp(r) },
              ...(r.status === "pending" ? [
                { key: "approve", icon: <CheckCircleOutlined />, label: t("stallApplications.approve"), onClick: () => { form.resetFields(); setReviewModal({ app: r, decision: "approve" }); } },
                { key: "reject", icon: <CloseCircleOutlined />, label: t("stallApplications.reject"), danger: true, onClick: () => { form.resetFields(); setReviewModal({ app: r, decision: "reject" }); } },
              ] : []),
            ],
          }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button
            className="admin-stall-application-more admin-data-more"
            type="text"
            aria-label={`${t("stallApplications.colActions")} ${r.stall_name}`}
            icon={<MoreOutlined style={{ fontSize: 18 }} />}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <AdminShell>
      <div className="admin-stall-applications-page admin-list-page">
        <div style={{ maxWidth: 1480, margin: "0 auto" }}>
          <div className="admin-stall-applications-toolbar">
            <div>
              <h1 className="admin-page-title">{t("stallApplications.title")}</h1>
              <p className="admin-stall-applications-subtitle">{t("stallApplications.pageSubtitle")}</p>
            </div>
            <Button className="admin-stall-applications-refresh" icon={<ReloadOutlined />} onClick={() => void refetch()}>
              {t("common.refresh")}
            </Button>
          </div>

          <div className="admin-stall-applications-stats admin-txn-stats grid">
            <ApplicationStatCard
              icon={<ShopOutlined />} iconColor={T.brand}
              badge={t("stallApplications.statBadgeOverall")} badgeColor={T.brandAmberText} badgeBg={T.warningBg}
              label={t("stallApplications.statTotal")} value={allSummary?.meta.total ?? 0}
            />
            <ApplicationStatCard
              icon={<ClockCircleOutlined />} iconColor={T.brand}
              badge={t("stallApplications.statBadgePending")} badgeColor={T.brandAmberText} badgeBg={T.warningBg}
              label={t("stallApplications.statPending")} value={pendingSummary?.meta.total ?? 0}
            />
            <ApplicationStatCard
              icon={<CheckCircleOutlined />} iconColor={T.successBright}
              badge={t("stallApplications.statBadgeApproved")} badgeColor={T.successText} badgeBg={T.successBg}
              label={t("stallApplications.statApproved")} value={approvedSummary?.meta.total ?? 0}
            />
            <ApplicationStatCard
              icon={<CloseCircleOutlined />} iconColor={T.dangerText}
              badge={t("stallApplications.statBadgeRejected")} badgeColor={T.dangerText} badgeBg={T.dangerBg}
              label={t("stallApplications.statRejected")} value={rejectedSummary?.meta.total ?? 0}
            />
          </div>

          <div className="admin-stall-applications-filters admin-txn-filter-panel">
            <Select
              className="admin-stall-applications-status-filter"
              placeholder={t("stallApplications.filterStatus")}
              allowClear
              value={statusFilter}
              onChange={(v) => { setStatusFilter(v); setPage(1); }}
              options={[
                { value: "pending", label: t("stallApplications.status.pending") },
                { value: "approved", label: t("stallApplications.status.approved") },
                { value: "rejected", label: t("stallApplications.status.rejected") },
              ]}
            />
          </div>

          <p className="admin-stall-applications-results">{t("common.total", { count: data?.meta.total ?? 0 })}</p>
          <div className="admin-stall-applications-table-panel admin-data-table-panel">
            <DataTable<StallApplication>
              rowKey="id"
              columns={columns}
              dataSource={data?.data ?? []}
              loading={isLoading}
              pagination={{
                current: page,
                pageSize: 10,
                total: data?.meta.total ?? 0,
                onChange: (p) => setPage(p),
                showTotal: (total) => t("common.total", { count: total }),
              }}
            />
          </div>
        </div>
      </div>

      {/* Detail view modal */}
      <Modal
        open={!!viewApp}
        title={<div className="admin-stall-application-dialog-title">{t("stallApplications.detailTitle")}</div>}
        footer={null}
        onCancel={() => setViewApp(null)}
        width={600}
      >
        {liveApp && (
          <div className="admin-stall-application-detail">
            <Descriptions bordered column={1} size="small">
              <Descriptions.Item label={t("stallApplications.colStallName")}>{liveApp.stall_name}</Descriptions.Item>
              <Descriptions.Item label={t("stallApplications.colCategory")}>{liveApp.category ?? "—"}</Descriptions.Item>
              <Descriptions.Item label={t("stallApplications.labelDescription")}>{liveApp.description ?? "—"}</Descriptions.Item>
              <Descriptions.Item label={t("stallApplications.colContact")}>{liveApp.contact_name}</Descriptions.Item>
              <Descriptions.Item label={t("stallApplications.labelContactPhone")}>{liveApp.contact_phone}</Descriptions.Item>
              <Descriptions.Item label={t("stallApplications.labelContactEmail")}>{liveApp.contact_email ?? "—"}</Descriptions.Item>
              <Descriptions.Item label={t("stallApplications.colStatus")}>
                <span
                  className="admin-data-badge"
                  style={{
                    color: (STATUS_STYLE[liveApp.status] ?? { color: T.textSecondary }).color,
                    background: (STATUS_STYLE[liveApp.status] ?? { bg: T.surfaceSection }).bg,
                  }}
                >
                  {t(`stallApplications.status.${liveApp.status}`, { defaultValue: liveApp.status })}
                </span>
              </Descriptions.Item>
              {liveApp.review_note && (
                <Descriptions.Item label={t("stallApplications.labelReviewNote")}>{liveApp.review_note}</Descriptions.Item>
              )}
              {liveApp.reviewer && (
                <Descriptions.Item label={t("stallApplications.colReviewer")}>{liveApp.reviewer.name}</Descriptions.Item>
              )}
              <Descriptions.Item label={t("stallApplications.labelAppliedAt")}>{formatDateTimeWithSeconds(liveApp.created_at, i18n.language)}</Descriptions.Item>
            </Descriptions>
            {liveApp.status === "pending" && (
              <div className="admin-stall-application-detail-actions">
                <Divider />
                <Space className="admin-stall-application-review-actions">
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={() => { setViewApp(null); form.resetFields(); setReviewModal({ app: liveApp, decision: "approve" }); }}
                  >
                    {t("stallApplications.approve")}
                  </Button>
                  <Button
                    danger
                    icon={<CloseCircleOutlined />}
                    onClick={() => { setViewApp(null); form.resetFields(); setReviewModal({ app: liveApp, decision: "reject" }); }}
                  >
                    {t("stallApplications.reject")}
                  </Button>
                </Space>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Review decision modal */}
      <Modal
        open={!!reviewModal}
        title={<div className="admin-stall-application-dialog-title">{reviewModal?.decision === "approve" ? t("stallApplications.approveModalTitle") : t("stallApplications.rejectModalTitle")}</div>}
        onCancel={() => setReviewModal(null)}
        onOk={() => form.submit()}
        okText={reviewModal?.decision === "approve" ? t("stallApplications.confirmApprove") : t("stallApplications.confirmReject")}
        okButtonProps={{
          danger: reviewModal?.decision === "reject",
          loading: review.isPending,
        }}
      >
        {reviewModal && (
          <div className="admin-stall-application-review-dialog">
            <Text className="admin-stall-application-review-description">
              {reviewModal.decision === "approve"
                ? t("stallApplications.approveDescription", { name: reviewModal.app.stall_name })
                : t("stallApplications.rejectDescription", { name: reviewModal.app.stall_name })}
            </Text>
            <Divider style={{ margin: "12px 0" }} />
            <Form className="admin-stall-application-review-form" form={form} layout="vertical" onFinish={handleReview}>
              {reviewModal.decision === "approve" && (
                <Form.Item name="sort_order" label={t("stallApplications.sortOrderLabel")}>
                  <Input type="number" min={0} placeholder="99" style={{ width: 120 }} />
                </Form.Item>
              )}
              <Form.Item
                name="review_note"
                label={reviewModal.decision === "reject" ? t("stallApplications.rejectionReason") : t("stallApplications.reviewNoteOptional")}
                rules={
                  reviewModal.decision === "reject"
                    ? [{ required: true, whitespace: true, min: 5, message: t("stallApplications.rejectionReason") }]
                    : []
                }
              >
                <TextArea
                  rows={3}
                  placeholder={reviewModal.decision === "reject"
                    ? t("stallApplications.rejectionPlaceholder")
                    : t("stallApplications.reviewNotePlaceholder")}
                />
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </AdminShell>
  );
}
